import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvents, useSeasons } from '@/api/queries';
import { Button } from '@/components/kit/Button';
import { PageHeader, Segments } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { EmptyState, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { CardLabel, MobileSub, Na, Table } from '@/components/kit/Table';
import { date, dateRange, num } from '@/lib/format';
import { EVENT_STATE } from './state';

/**
 * Список мероприятий.
 *
 * Чисел здесь ровно столько, сколько нужно для выбора: сколько
 * в продаже, сколько черновиков, что ближайшее. Как идут продажи —
 * на дашборде и в отчётах: если рядом с числом нет поля, которое
 * человек тут же меняет, числу в настройке не место (БЛ §1).
 */
export function EventsListPage() {
  const navigate = useNavigate();
  const seasons = useSeasons();
  const [seasonId, setSeasonId] = useState<string | null>(null);

  const current = seasonId ?? seasons.data?.[0]?.id;
  const events = useEvents(current);

  if (seasons.isError || events.isError) {
    return (
      <div className="main">
        <PageHeader title="Мероприятия" />
        <Panel>
          <ErrorState
            message={(seasons.error ?? events.error)?.message ?? 'Не удалось получить список.'}
            onRetry={() => {
              void seasons.refetch();
              void events.refetch();
            }}
          />
        </Panel>
      </div>
    );
  }

  if (seasons.isLoading || events.isLoading) {
    return (
      <div className="main">
        <PageHeader title="Мероприятия" />
        <Panel flush>
          <TableSkeleton rows={5} cols={5} />
        </Panel>
      </div>
    );
  }

  const list = events.data ?? [];
  const live = list.filter((e) => e.state === 'live');
  const drafts = list.filter((e) => e.state === 'draft');
  const nearest = live.map((e) => e.dateFrom).sort()[0];

  return (
    <div className="main">
      <PageHeader title="Мероприятия" count={list.length}>
        <Button tone="primary" onClick={() => navigate('/events/new')}>
          ＋ Мероприятие
        </Button>
      </PageHeader>

      {seasons.data && seasons.data.length > 0 ? (
        <Segments
          value={current ?? ''}
          onChange={setSeasonId}
          items={seasons.data.map((s) => ({
            value: s.id,
            label: s.name,
            count: s.eventCount,
          }))}
        />
      ) : null}

      {/* Срез по выбранному сезону — те же условия, что у таблицы (кит 2.12) */}
      <MetricRow
        variant="slice"
        items={[
          { label: 'В продаже', value: num(live.length), dim: !live.length },
          { label: 'Черновиков', value: num(drafts.length), dim: !drafts.length },
          { label: 'Ближайшее', value: nearest ? date(nearest) : '—', dim: !nearest },
        ]}
      />

      <Panel flush>
        {list.length === 0 ? (
          <EmptyState>
            В этом сезоне пока пусто.
            <br />
            Заведите первое мероприятие — город, даты, программу
          </EmptyState>
        ) : (
          <Table
            valueWidth={130}
            head={
              <tr>
                <th className="srt">
                  Город <span className="ar">▲</span>
                </th>
                <th>Даты</th>
                <th>Площадка</th>
                <th>Состояние</th>
                <th className="r">Лимит команд</th>
                <th className="r">Заявки до</th>
                <th />
              </tr>
            }
          >
            {list.map((e) => {
              const state = EVENT_STATE[e.state];
              return (
                <tr key={e.id} className="clk" onClick={() => navigate(`/events/${e.id}/main`)}>
                  <td data-col="primary">
                    <span className="nm">{e.city}</span>
                    <MobileSub>
                      {dateRange(e.dateFrom, e.dateTo)}
                      {e.venue ? ` · ${e.venue}` : ''}
                    </MobileSub>
                  </td>
                  <td className="muted num" data-col="hide">
                    {dateRange(e.dateFrom, e.dateTo)}
                  </td>
                  <td className="muted ell" data-col="hide">
                    {e.venue ?? <Na />}
                  </td>
                  <td data-col="secondary">
                    <Pill tone={state.tone}>{state.label}</Pill>
                  </td>
                  <td className="r" data-col="sec2">
                    <CardLabel>лимит</CardLabel>
                    {e.teamLimitTotal ? (
                      <span className="num">{num(e.teamLimitTotal)}</span>
                    ) : (
                      <span className="na">—</span>
                    )}
                  </td>
                  <td className="r num muted" data-col="hide">
                    {date(e.applyUntil)}
                  </td>
                  <td className="r" data-col="act" />
                </tr>
              );
            })}
          </Table>
        )}
      </Panel>

      <Note>
        Мероприятие в состоянии <b>черновик</b> видно только внутри: продажи по нему не работают,
        заявки не принимаются
      </Note>
      <Note>
        <b>Здесь только заведение.</b> Сколько заявлено и как идут продажи — на дашборде главной
        и в отчётах
      </Note>
    </div>
  );
}
